// frontend/src/pages/TermsOfServicePage.jsx
import { useState, useEffect } from 'react';
import { GlobalStyles } from '@bigcommerce/big-design';
import { FileText, CheckCircle, AlertTriangle, Shield, CreditCard, Mail, Menu, X } from 'lucide-react';

const colors = {
  indigo50: '#eef2ff',
  indigo100: '#e0e7ff',
  indigo600: '#4f46e5',
  indigo900: '#312e81',
  gray50: '#f9fafb',
  gray100: '#f3f4f6',
  gray200: '#e5e7eb',
  gray300: '#d1d5db',
  gray400: '#9ca3af',
  gray500: '#6b7280',
  gray600: '#4b5563',
  gray700: '#374151',
  gray900: '#111827',
  white: '#ffffff',
};

const styles = {
  page: {
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    color: colors.gray900,
    backgroundColor: colors.white,
    margin: '-2rem',
    minHeight: '100vh',
  },
  // Navigation
  nav: {
    position: 'absolute',
    top: '2rem',
    left: 0,
    right: 0,
    zIndex: 50,
    display: 'flex',
    justifyContent: 'center',
    padding: '0 2rem',
  },
  navInner: {
    display: 'flex',
    width: '100%',
    maxWidth: '80rem',
    padding: '1rem 1.5rem',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  navLinks: {
    display: 'flex',
    alignItems: 'center',
    gap: '2rem',
    fontSize: '0.875rem',
    fontWeight: 600,
  },
  navLink: {
    color: 'rgba(255,255,255,0.9)',
    textDecoration: 'none',
    transition: 'color 0.2s',
    cursor: 'pointer',
  },
  navRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '2rem',
  },
  navButton: {
    background: colors.white,
    color: colors.gray900,
    padding: '0.625rem 1.5rem',
    borderRadius: '9999px',
    fontSize: '0.75rem',
    fontWeight: 700,
    border: 'none',
    cursor: 'pointer',
    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
    transition: 'transform 0.2s',
    textDecoration: 'none',
  },
  hamburger: {
    display: 'none',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '0.5rem',
  },
  mobileMenu: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: colors.white,
    zIndex: 100,
    display: 'flex',
    flexDirection: 'column',
    padding: '2rem',
  },
  mobileMenuHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '3rem',
  },
  mobileMenuClose: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '0.5rem',
  },
  mobileMenuLinks: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
  },
  mobileMenuLink: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: colors.gray900,
    textDecoration: 'none',
    padding: '0.75rem 0',
    borderBottom: `1px solid ${colors.gray100}`,
  },
  mobileMenuButton: {
    background: `linear-gradient(to right, ${colors.indigo600}, #9333ea)`,
    color: colors.white,
    padding: '1rem 2rem',
    borderRadius: '9999px',
    fontSize: '1rem',
    fontWeight: 700,
    border: 'none',
    cursor: 'pointer',
    marginTop: '2rem',
    textAlign: 'center',
    textDecoration: 'none',
  },
  header: {
    background: `linear-gradient(to right, ${colors.indigo600}, #9333ea)`,
    padding: '10rem 2rem 4rem 2rem',
    color: colors.white,
    position: 'relative',
  },
  headerContainer: {
    maxWidth: '64rem',
    margin: '0 auto',
  },
  headerTitle: {
    fontSize: '2.5rem',
    fontWeight: 700,
    margin: 0,
    marginBottom: '0.5rem',
  },
  headerSubtitle: {
    fontSize: '1.125rem',
    opacity: 0.9,
    margin: 0,
  },
  content: {
    maxWidth: '64rem',
    margin: '0 auto',
    padding: '3rem 2rem',
  },
  section: {
    marginBottom: '3rem',
  },
  sectionTitle: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: colors.gray900,
    marginBottom: '1.5rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  },
  sectionIcon: {
    width: '2.5rem',
    height: '2.5rem',
    background: `linear-gradient(to right, ${colors.indigo600}, #9333ea)`,
    borderRadius: '0.5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  card: {
    background: colors.white,
    border: `1px solid ${colors.gray200}`,
    borderRadius: '0.75rem',
    padding: '1.5rem',
    marginBottom: '1rem',
  },
  cardTitle: {
    fontSize: '1.125rem',
    fontWeight: 600,
    color: colors.gray900,
    marginBottom: '0.75rem',
  },
  cardText: {
    color: colors.gray600,
    lineHeight: 1.7,
    margin: 0,
  },
  list: {
    color: colors.gray600,
    lineHeight: 1.7,
    marginTop: '1rem',
    paddingLeft: '1.25rem',
  },
  listItem: {
    marginBottom: '0.5rem',
  },
  lastUpdated: {
    fontSize: '0.875rem',
    color: colors.gray500,
    marginTop: '1rem',
  },
  footer: {
    padding: '3rem 1.5rem',
    background: colors.white,
    borderTop: `1px solid ${colors.gray200}`,
  },
  footerContainer: {
    maxWidth: '80rem',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '2rem',
  },
  footerLinks: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '2rem',
    fontSize: '0.875rem',
    fontWeight: 600,
    color: colors.gray600,
    justifyContent: 'center',
    alignItems: 'center',
  },
  footerLink: {
    color: colors.gray600,
    textDecoration: 'none',
    transition: 'color 0.2s',
  },
  footerDot: {
    color: colors.gray300,
  },
  footerCopyright: {
    fontSize: '0.75rem',
    color: colors.gray400,
    textAlign: 'center',
    margin: 0,
  },
};

function TermsOfServicePage() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  return (
    <>
      <GlobalStyles />
      <div style={styles.page}>
        {/* Mobile Menu Overlay */}
        {mobileMenuOpen && (
          <div style={styles.mobileMenu}>
            <div style={styles.mobileMenuHeader}>
              <img src="/logo-checkout-fees.png" alt="Checkout Fees" style={{ height: '42px' }} />
              <button style={styles.mobileMenuClose} onClick={closeMobileMenu}>
                <X size={28} color={colors.gray900} />
              </button>
            </div>
            <div style={styles.mobileMenuLinks}>
              <a href="/#features" style={styles.mobileMenuLink} onClick={closeMobileMenu}>Features</a>
              <a href="/#how-it-works" style={styles.mobileMenuLink} onClick={closeMobileMenu}>How It Works</a>
              <a href="/#use-cases" style={styles.mobileMenuLink} onClick={closeMobileMenu}>Use Cases</a>
              <a href="/support" style={styles.mobileMenuLink} onClick={closeMobileMenu}>Documentation</a>
            </div>
            <a href="/support#contact" style={styles.mobileMenuButton} onClick={closeMobileMenu}>Support</a>
          </div>
        )}

        {/* Header */}
        <header style={{
          ...styles.header,
          ...(isMobile && { padding: '8rem 3rem 3rem 3rem' })
        }}>
          {/* Navigation */}
          <nav style={styles.nav}>
            <div style={styles.navInner}>
              <a href="/">
                <img src="/logo-checkout-light.png" alt="Checkout Fees" style={{ height: '42px' }} />
              </a>

              {/* Desktop Navigation */}
              {!isMobile && (
                <>
                  <div style={styles.navLinks}>
                    <a href="/#features" style={styles.navLink}>Features</a>
                    <a href="/#how-it-works" style={styles.navLink}>How It Works</a>
                    <a href="/#use-cases" style={styles.navLink}>Use Cases</a>
                  </div>
                  <div style={styles.navRight}>
                    <a href="/support" style={styles.navLink}>Documentation</a>
                    <a href="/support#contact" style={styles.navButton}>Support</a>
                  </div>
                </>
              )}

              {/* Mobile Hamburger */}
              {isMobile && (
                <button style={{ ...styles.hamburger, display: 'block' }} onClick={() => setMobileMenuOpen(true)}>
                  <Menu size={28} color={colors.white} />
                </button>
              )}
            </div>
          </nav>

          <div style={styles.headerContainer}>
            <img src="/logo-checkout-light.png" alt="Checkout Fees" style={{ height: '48px', display: 'block', margin: '0 auto 1rem auto' }} />
            <h1 style={{
              ...styles.headerTitle,
              textAlign: 'center',
              ...(isMobile && { fontSize: '1.75rem' })
            }}>Terms of Service</h1>
            <p style={{ ...styles.headerSubtitle, textAlign: 'center' }}>
              Please read these terms carefully before using Checkout Fees
            </p>
          </div>
        </header>

        {/* Content */}
        <main style={{
          ...styles.content,
          ...(isMobile && { padding: '2rem 3rem' })
        }}>
          <p style={styles.lastUpdated}>Last updated: August 15, 2026</p>

          {/* Agreement */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>
              <div style={styles.sectionIcon}>
                <FileText size={20} color={colors.white} />
              </div>
              Agreement to Terms
            </h2>
            <div style={styles.card}>
              <p style={styles.cardText}>
                These Terms of Service ("Terms") govern your use of the Checkout Fees application ("App") developed by Prangano.com, an extension of Inova Creative Agency ("we," "our," or "us").
              </p>
              <p style={{ ...styles.cardText, marginTop: '1rem' }}>
                By installing, accessing, or using Checkout Fees, you agree to be bound by these Terms. If you do not agree to these Terms, do not install or use the App.
              </p>
            </div>
          </section>

          {/* Description of Service */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>
              <div style={styles.sectionIcon}>
                <CheckCircle size={20} color={colors.white} />
              </div>
              Description of Service
            </h2>
            <div style={styles.card}>
              <p style={styles.cardText}>
                Checkout Fees is a BigCommerce application that allows merchants to add custom fees to their checkout process. The App provides:
              </p>
              <ul style={styles.list}>
                <li style={styles.listItem}>Fixed amount and percentage-based fee creation</li>
                <li style={styles.listItem}>Native integration with BigCommerce's standard checkout for fee display, with an optional custom checkout mode for advanced use cases</li>
                <li style={styles.listItem}>Fee targeting by category, product, brand, customer group, and channel</li>
                <li style={styles.listItem}>Automatic inclusion of fees in order records and customer emails</li>
              </ul>
            </div>
          </section>

          {/* User Responsibilities */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>
              <div style={styles.sectionIcon}>
                <AlertTriangle size={20} color={colors.white} />
              </div>
              User Responsibilities
            </h2>
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>You agree to:</h3>
              <ul style={styles.list}>
                <li style={styles.listItem}>Use the App only for lawful purposes and in compliance with all applicable laws and regulations</li>
                <li style={styles.listItem}>Ensure any fees you create comply with local, state, and federal laws regarding surcharges and additional fees</li>
                <li style={styles.listItem}>Clearly disclose all fees to your customers as required by law</li>
                <li style={styles.listItem}>Maintain the security of your BigCommerce account credentials</li>
                <li style={styles.listItem}>Not use the App in any way that could damage, disable, or impair our services</li>
              </ul>
            </div>
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Legal Compliance</h3>
              <p style={styles.cardText}>
                You are solely responsible for ensuring that your use of checkout fees complies with all applicable laws, including but not limited to consumer protection laws, credit card surcharge regulations, and tax requirements in your jurisdiction. Some regions restrict or prohibit certain types of surcharges.
              </p>
            </div>
          </section>

          {/* Fees and Payment */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>
              <div style={styles.sectionIcon}>
                <CreditCard size={20} color={colors.white} />
              </div>
              Fees and Payment
            </h2>
            <div style={styles.card}>
              <p style={styles.cardText}>
                Checkout Fees includes a 14-day free trial with no credit card required. Continued use after the trial requires an active subscription, billed monthly through your BigCommerce account via BigCommerce's Unified Billing. If you cancel, the App continues to function through the end of the billing period you've already paid for; BigCommerce does not issue a refund for the unused remainder, and App functionality pauses after that date until you resubscribe. We reserve the right to modify pricing with reasonable notice to existing users.
              </p>
            </div>
          </section>

          {/* Disclaimer of Warranties */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>
              <div style={styles.sectionIcon}>
                <Shield size={20} color={colors.white} />
              </div>
              Disclaimer of Warranties
            </h2>
            <div style={styles.card}>
              <p style={styles.cardText}>
                THE APP IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
              </p>
              <p style={{ ...styles.cardText, marginTop: '1rem' }}>
                We do not warrant that the App will be uninterrupted, error-free, or completely secure. We are not responsible for any losses or damages arising from your use of the App, including but not limited to lost profits, lost data, or business interruption.
              </p>
              <p style={{ ...styles.cardText, marginTop: '1rem' }}>
                Checkout Fees relies on real-time communication with BigCommerce's systems to calculate and deliver fees to your checkout. While we continuously monitor and improve the reliability of fee delivery, including through redundant delivery mechanisms and automated checks, we cannot guarantee that fees will apply, display, or calculate correctly in every circumstance, including due to BigCommerce platform issues, network interruptions, or unforeseen errors. We are not liable for any lost revenue, uncollected fees, or other financial losses resulting from a fee failing to apply or display correctly at checkout.
              </p>
            </div>
          </section>

          {/* Limitation of Liability */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>
              <div style={styles.sectionIcon}>
                <AlertTriangle size={20} color={colors.white} />
              </div>
              Limitation of Liability
            </h2>
            <div style={styles.card}>
              <p style={styles.cardText}>
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT SHALL WE BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, OR BUSINESS OPPORTUNITIES, ARISING OUT OF OR RELATED TO YOUR USE OF THE APP.
              </p>
              <p style={{ ...styles.cardText, marginTop: '1rem' }}>
                Our total liability for any claims arising from your use of the App shall not exceed the amount you paid us, if any, for the App during the twelve (12) months preceding the claim.
              </p>
            </div>
          </section>

          {/* Termination */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>
              <div style={styles.sectionIcon}>
                <FileText size={20} color={colors.white} />
              </div>
              Termination
            </h2>
            <div style={styles.card}>
              <p style={styles.cardText}>
                You may stop using the App at any time by uninstalling it from your BigCommerce store. We reserve the right to suspend or terminate your access to the App at any time, with or without cause, with or without notice.
              </p>
              <p style={{ ...styles.cardText, marginTop: '1rem' }}>
                Upon termination, your right to use the App will immediately cease. Any fees already applied to orders will remain in your order history.
              </p>
            </div>
          </section>

          {/* Changes to Terms */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>
              <div style={styles.sectionIcon}>
                <FileText size={20} color={colors.white} />
              </div>
              Changes to Terms
            </h2>
            <div style={styles.card}>
              <p style={styles.cardText}>
                We may update these Terms from time to time. We will notify you of any material changes by posting the new Terms on this page and updating the "Last updated" date. Your continued use of the App after any changes constitutes acceptance of the new Terms.
              </p>
            </div>
          </section>

          {/* Contact */}
          <section style={{ ...styles.section, marginBottom: 0 }}>
            <h2 style={styles.sectionTitle}>
              <div style={styles.sectionIcon}>
                <Mail size={20} color={colors.white} />
              </div>
              Contact Us
            </h2>
            <div style={{
              ...styles.card,
              background: colors.indigo50,
              border: `1px solid ${colors.indigo100}`,
              textAlign: 'center',
              padding: '2rem',
            }}>
              <p style={{ ...styles.cardText, color: colors.gray700 }}>
                If you have questions about these Terms of Service, please contact us at{' '}
                <a href="mailto:hello@prangano.com" style={{ color: colors.indigo600, fontWeight: 600 }}>
                  hello@prangano.com
                </a>
              </p>
            </div>
          </section>
        </main>

        {/* Footer */}
        <footer style={styles.footer}>
          <div style={styles.footerContainer}>
            <img src="/logo-checkout-fees.png" alt="Checkout Fees" style={{ height: '40px' }} />
            <div style={{
              ...styles.footerLinks,
              ...(isMobile && { flexDirection: 'column', gap: '1rem' })
            }}>
              <a href="https://www.bigcommerce.com/apps/checkout-fees-by-prangano/" target="_blank" rel="noopener noreferrer" style={styles.footerLink}>BigCommerce App Store</a>
              {!isMobile && <span style={styles.footerDot}>•</span>}
              <a href="/support" style={styles.footerLink}>Documentation</a>
              {!isMobile && <span style={styles.footerDot}>•</span>}
              <a href="/support#contact" style={styles.footerLink}>Support</a>
              {!isMobile && <span style={styles.footerDot}>•</span>}
              <a href="/privacy" style={styles.footerLink}>Privacy Policy</a>
              {!isMobile && <span style={styles.footerDot}>•</span>}
              <a href="/terms" style={styles.footerLink}>Terms of Service</a>
            </div>
            <p style={styles.footerCopyright}>© 2026 Checkout Fees. All rights reserved.</p>
          </div>
        </footer>
      </div>
    </>
  );
}

export default TermsOfServicePage;
